import { appAudioFocus, type AppAudioLease } from "@/lib/appAudioFocus";
import { useState, useCallback, useRef, useEffect } from "react";

const LANG_MAP: Record<string, string> = { EN: "en-US", "PT-BR": "pt-BR", NL: "nl-NL" };

interface RecognitionResultEvent {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; [index: number]: { transcript: string } }>;
}
interface Recognition {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  onresult: ((event: RecognitionResultEvent) => void) | null;
  onerror: ((event: { error?: string; message?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type RecognitionConstructor = new () => Recognition;

export const useVoiceInput = (lang: string) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  // Consumers toast genuine recognition failures. Cancellation clears this
  // signal and ignores the browser's expected "aborted" error.
  const [error, setError] = useState<{ code: string; at: number } | null>(null);
  const focusRef = useRef<AppAudioLease | null>(null);
  const recognitionRef = useRef<Recognition | null>(null);
  const finalTranscriptRef = useRef("");
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  const supported = typeof window !== "undefined" && ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  const retireRecognition = useCallback((operation: "abort" | "stop") => {
    focusRef.current?.release(); focusRef.current = null;
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    generationRef.current++;
    if (!recognition) return;
    // Detach before abort/stop: browsers may synchronously emit error/end.
    recognition.onresult = null; recognition.onerror = null; recognition.onend = null;
    try { recognition[operation](); } catch { /* Already ended. */ }
  }, []);
  const cancelListening = useCallback(() => {
    retireRecognition("abort");
    finalTranscriptRef.current = "";
    if (mountedRef.current) { setIsListening(false); setTranscript(""); setError(null); }
  }, [retireRecognition]);

  const startListening = useCallback(() => {
    if (!mountedRef.current) return false;
    if (!supported) { setError({ code: "unsupported", at: Date.now() }); return false; }
    cancelListening();
    const constructors = window as typeof window & { SpeechRecognition?: RecognitionConstructor; webkitSpeechRecognition?: RecognitionConstructor };
    const SpeechRecognition = constructors.SpeechRecognition || constructors.webkitSpeechRecognition;
    if (!SpeechRecognition) { setError({ code: "unsupported", at: Date.now() }); return false; }
    const recognition = new SpeechRecognition();
    const generation = generationRef.current;
    const current = () => mountedRef.current && recognitionRef.current === recognition && generationRef.current === generation;
    recognition.lang = LANG_MAP[lang] || "en-US";
    recognition.interimResults = true; recognition.maxAlternatives = 1; recognition.continuous = true;
    recognition.onresult = event => {
      if (!current()) return;
      let finalPart = "", interimPart = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalPart += event.results[i][0].transcript;
        else interimPart += event.results[i][0].transcript;
      }
      if (finalPart) {
        // Engines need not prefix the next final result with a space.
        const prev = finalTranscriptRef.current;
        const needsSpace = prev !== "" && !/\s$/.test(prev) && !/^\s/.test(finalPart);
        finalTranscriptRef.current = needsSpace ? prev + " " + finalPart : prev + finalPart;
      }
      setTranscript(finalTranscriptRef.current + interimPart);
    };
    recognition.onerror = event => {
      if (!current()) return;
      console.error("[voice:DIAG] recognition onerror", { error: event?.error, message: event?.message });
      setError({ code: String(event?.error || "unknown"), at: Date.now() });
      setIsListening(false);
      retireRecognition("abort");
    };
    recognition.onend = () => {
      if (!current()) return;
      focusRef.current?.release(); focusRef.current = null;
      recognitionRef.current = null; generationRef.current++;
      recognition.onresult = null; recognition.onerror = null; recognition.onend = null;
      setIsListening(false);
    };
    focusRef.current = appAudioFocus.acquire("capture");
    recognitionRef.current = recognition;
    try { recognition.start(); } catch (err) {
      console.error("[voice:DIAG] recognition.start() threw", err);
      retireRecognition("abort"); setIsListening(false); return false;
    }
    if (!current()) return false;
    setIsListening(true);
    return true;
  }, [lang, supported, cancelListening, retireRecognition]);

  const stopListening = useCallback((): string => {
    const finalText = finalTranscriptRef.current;
    retireRecognition("stop");
    finalTranscriptRef.current = "";
    if (mountedRef.current) { setIsListening(false); setTranscript(""); }
    return finalText.trim();
  }, [retireRecognition]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; cancelListening(); };
  }, [cancelListening]);

  return { isListening, transcript, startListening, stopListening, cancelListening, supported, error };
};
